import React from 'react';
import { ArrowRight, Droplets, Wind, Wrench, Zap, ThermometerSun, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';
import PageSEO from '@/components/PageSEO';
import { ReplacementPageLinks } from '@/components/InternalLinks';

const AcLifespanPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PageSEO breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: 'AC Lifespan in Louisiana', url: '/ac-unit-lifespan-louisiana' },
      ]} />
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="ac-lifespan-heading"
          >
            How Long Do AC Units Last in Louisiana?
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Louisiana's heat and humidity take a toll on air conditioning systems. Learn what
            affects your AC's lifespan and when it's time to consider a replacement.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Get an Instant Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Average Lifespan */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Average AC Lifespan in Louisiana
          </h2>
          <div className="bg-slate-50 rounded-2xl p-8 mb-8">
            <p className="text-slate-600 text-lg mb-4">
              While the national average lifespan for a central AC unit is 15 to 20 years, Louisiana
              homeowners typically see 12 to 15 years of reliable service. The difference comes down
              to climate — our systems work harder and longer than anywhere else in the country.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="bg-white p-5 rounded-xl border border-slate-200">
                <p className="text-sm text-slate-500 mb-1">National Average</p>
                <p className="text-2xl font-bold text-slate-400">15–20 years</p>
              </div>
              <div className="bg-white p-5 rounded-xl border-2 border-[#0EA5E9]">
                <p className="text-sm text-[#0EA5E9] mb-1">Louisiana Average</p>
                <p className="text-2xl font-bold text-[#0F172A]">12–15 years</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Humidity Impact */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Droplets className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The Humidity Factor
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-lg">
            <p>
              Louisiana consistently ranks among the most humid states in the U.S. Relative humidity
              regularly exceeds 80% during summer months. This places enormous strain on your AC system
              because it must remove both heat and moisture from the air.
            </p>
            <p>
              When your system runs in high-humidity conditions for extended periods, several things happen:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {[
              { title: 'Compressor Strain', desc: 'The compressor runs longer cycles to manage moisture, accelerating wear.' },
              { title: 'Coil Corrosion', desc: 'Condensation on evaporator coils promotes rust and degradation over time.' },
              { title: 'Mold Growth', desc: 'Excess moisture in ductwork and drain pans creates mold and efficiency loss.' },
              { title: 'Higher Energy Bills', desc: 'Systems work harder to maintain setpoint, increasing operating costs.' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-5 rounded-xl">
                <h3 className="font-bold text-[#0F172A] mb-1">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Salt Air Impact */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Wind className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Salt Air and Coastal Corrosion
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-lg">
            <p>
              Homes near the Gulf Coast — including much of the Greater New Orleans area, Kenner, and
              Slidell — are exposed to salt-laden air. Salt accelerates corrosion on the outdoor condenser
              unit's aluminum fins and copper tubing.
            </p>
            <p>
              Even homes 20–30 miles from the coast can experience salt air effects during storms and
              strong southerly winds. Annual coil cleaning and protective coatings can extend your unit's
              life, but corrosion eventually takes its toll.
            </p>
          </div>
          <div className="mt-6 bg-[#0F172A] rounded-2xl p-6 text-white">
            <p className="font-medium mb-2">Pro Tip from Local Contractors:</p>
            <p className="text-slate-300 text-sm">
              If you live within 15 miles of the coast, consider a unit with a salt-resistant coating
              (like Carrier's WeatherArmor or Trane's Spine Fin coil) to maximize lifespan.
            </p>
          </div>
        </div>
      </section>

      {/* Repair vs Replace */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Wrench className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When to Repair vs. Replace
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-lg mb-6">
            <p>
              A common rule of thumb is the "5,000 Rule": multiply the age of your unit by the cost of
              the repair. If the result exceeds $5,000, replacement is usually the smarter investment.
            </p>
          </div>
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
            <div className="grid grid-cols-3 bg-[#0F172A] text-white text-sm font-bold">
              <div className="p-4">Unit Age</div>
              <div className="p-4">Repair Cost</div>
              <div className="p-4">Recommendation</div>
            </div>
            {[
              { age: '5 years', cost: '$800', rec: 'Repair', color: 'text-green-600' },
              { age: '8 years', cost: '$600', rec: 'Repair (borderline)', color: 'text-yellow-600' },
              { age: '10 years', cost: '$500+', rec: 'Consider replacing', color: 'text-orange-600' },
              { age: '12+ years', cost: 'Any major repair', rec: 'Replace', color: 'text-red-600' },
            ].map((row, i) => (
              <div key={i} className={`grid grid-cols-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="p-4 text-[#0F172A] font-medium">{row.age}</div>
                <div className="p-4 text-slate-600">{row.cost}</div>
                <div className={`p-4 font-semibold ${row.color}`}>{row.rec}</div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Want a detailed breakdown?{' '}
            <a href="/repair-vs-replace-ac" className="text-[#0EA5E9] hover:underline">
              Read our full Repair vs. Replace guide
            </a>.
          </p>
        </div>
      </section>

      {/* Energy Efficiency */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Energy Efficiency Gains with a New System
            </h2>
          </div>
          <div className="prose prose-slate max-w-none">
            <p>
              AC units manufactured before 2015 typically have a SEER rating of 10–13. Modern systems
              offer SEER ratings of 15–22+, which can reduce cooling costs by 30–50%. In Louisiana,
              where AC accounts for a significant portion of your energy bill, upgrading to a high-efficiency
              system pays for itself faster.
            </p>
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="prose prose-slate max-w-none">
            <p>
              Not sure of your unit's age? Use our{' '}
              <a href="/check-ac-age" className="text-[#0EA5E9]">AC age checker</a>{' '}
              to decode your serial number. Ready to see replacement costs?{' '}
              Get a free{' '}
              <a href="/instant-hvac-estimate-new-orleans" className="text-[#0EA5E9]">instant HVAC estimate</a>{' '}
              in minutes — no phone call required.
            </p>
            <p>
              Compare{' '}
              <a href="/hvac-pricing-new-orleans" className="text-[#0EA5E9]">HVAC pricing in New Orleans</a>{' '}
              or explore{' '}
              <a href="/ac-replacement-cost-new-orleans" className="text-[#0EA5E9]">AC replacement costs</a>{' '}
              for your area.
            </p>
          </div>
        </div>
      </section>

      <RelatedArticles currentPath="/ac-unit-lifespan-louisiana" />

      <ReplacementPageLinks />

      {/* CTA */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to See Your Replacement Options?
          </h2>
          <p className="text-slate-300 mb-8">
            Get accurate HVAC pricing for your Louisiana home. Free estimate in minutes.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg">
              Start My Instant Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AcLifespanPage;
